import { Alert, Icons } from "@texada/ui";
import { ExcelExportParams } from "ag-grid-community";
import axios from "axios";
import { INTERNAL_SERVER_ERROR, MULTI_STATUS } from "http-status-codes";
import i18n from "i18n/i18n";
import Uri from "jsuri";
import { capitalize, get, isEmpty, isEqual, isNil, upperFirst } from "lodash";
import moment from "moment";
import React from "react";

import {
  ALERT_ERROR_TIMEOUT,
  CONTRACTS_URL,
  SCS_SRM_UID,
  SERVER_ERROR_500,
  TICKET_ASSIGN_URL,
  UPDATE_CONTRACT_URL
} from "constants/AppConstants";
import { useBarcodeScanner } from "helpers/BarcodeScanner";
import {
  DOCUMENT_TYPE,
  PAGE_NUMBER,
  SrmBoolean,
  TICKET_STATUS,
  TRANSACTION_TYPE,
  getApiErrorMsg,
  getTicketFullAddress,
  hasProperty
} from "helpers/helper";
import { FlaskReq } from "helpers/Request";
import { SRM_DATE_TIME_FORMAT } from "helpers/timeHelpers";
import { getFullAddress } from "pages/contracts/contractUtils.tsx";
import {
  SchedulingTicket,
  TicketDetails
} from "./scheduling/TicketSchedulingContext";

export const ticketTypes = {
  PICK_UP: "pickup",
  DELIVERY: "delivery"
};

export function formatTicketNumber({
  contract_n,
  ticket_n,
  invoice_n
}: {
  contract_n: number | string;
  ticket_n: number | string;
  invoice_n?: number | string;
}) {
  if (contract_n) {
    return `${contract_n}-${ticket_n}`;
  } else if (invoice_n) {
    return `${invoice_n}-${ticket_n}`;
  }
  return ticket_n;
}

export function getUnassignedTickets(tickets: SchedulingTicket[]) {
  return tickets.filter(
    ticket =>
      ![TICKET_STATUS.COMPLETE, TICKET_STATUS.CLOSED].includes(
        ticket.ticket_details.status
      ) && ticket.driver_id === -1
  );
}

export function getTicketDetailsTypeIcon(
  type: TicketDetails["ticket_type"]
): React.ReactNode {
  switch (type) {
    case "pickup":
      return <Icons.PickupTicket />;
    case "delivery":
      return <Icons.DeliveryTicket />;
    case "task":
      return <Icons.TaskTicket />;
    default:
      return null;
  }
}

export const TicketTypeMap: {
  [key in Models.TicketType | Models.TicketDetailType]: Models.TicketDetailType;
} = {
  P: "pickup",
  D: "delivery",
  T: "task",
  pickup: "pickup",
  delivery: "delivery",
  task: "task"
};

export function getTicketType(
  type: Models.TicketType | Models.TicketDetailType
) {
  return upperFirst(TicketTypeMap[type]);
}

export const ticketAddressField = [
  "origin_name",
  "origin_addr1",
  "origin_addr2",
  "origin_city",
  "origin_prov_state",
  "origin_postal_zip",
  "origin_phone",
  "destination_name",
  "destination_addr1",
  "destination_addr2",
  "destination_city",
  "destination_prov_state",
  "destination_postal_zip",
  "destination_phone"
];

export function validateTicketAddress(postData: Models.TicketAddress) {
  let errors: { origin?: string; destination?: string } = {};
  if (isEmpty(postData?.origin_addr1?.trim())) {
    errors.origin = i18n.t("origin_address_is_required");
  }
  if (isEmpty(postData?.destination_addr1?.trim())) {
    errors.destination = i18n.t("destination_address_is_required");
  }
  return errors;
}

export const processTicketCell: ExcelExportParams["processCellCallback"] =
  cell => {
    if (cell.column.getDefinition().headerName === "Products") {
      const { data } = cell.node;
      const ticket_details = getTicketFromTicketSchedule({ ticket: data });
      const assets = hasProperty(data, "ticketasset_set")
        ? data?.ticketasset_set
        : get(ticket_details, "ticketasset_set", []);

      if (Array.isArray(assets)) {
        return assets
          .map(a => `${a.prod_n} ${a.title}(${a.quantity})`)
          .join("\n");
      }
      return cell.value;
    }
    return cell.value;
  };

export async function fetchTicketOriginDestination(
  ticketType,
  location,
  shipInfo
) {
  const res = await FlaskReq(
    {
      method: "get",
      url: new Uri(
        `${localStorage.flask_url}/inventory/locations/${location || " "}/`
      )
    },
    { showMessage: false }
  );
  let originAddress = {};
  let destinationAddress = {};
  const inventoryAddress = {
    name: res.data.divisionName,
    city: res.data.address.city,
    provState: res.data.address.province,
    postalZip: res.data.address.postalCode,
    address1: res.data.address.address1,
    address2: res.data.address.address2,
    phone: res.data.address.phoneNumber
  };
  if (ticketType === DOCUMENT_TYPE.PICKUP) {
    originAddress = shipInfo;
    destinationAddress = inventoryAddress;
  } else {
    originAddress = inventoryAddress;
    destinationAddress = shipInfo;
  }
  return {
    origin_name: originAddress?.name,
    origin_addr1: originAddress?.address1,
    origin_addr2: originAddress?.address2,
    origin_city: originAddress?.city,
    origin_prov_state: originAddress?.provState,
    origin_postal_zip: originAddress?.postalZip,
    origin_phone: originAddress?.phone,
    destination_name: destinationAddress?.name,
    destination_addr1: destinationAddress?.address1,
    destination_addr2: destinationAddress?.address2,
    destination_city: destinationAddress?.city,
    destination_prov_state: destinationAddress?.provState,
    destination_postal_zip: destinationAddress?.postalZip,
    destination_phone: destinationAddress?.phone
  };
}

export function getLineoutDate(contract, asset_list) {
  let lineOutDate = moment(
    `${contract.dateOut} ${contract.timeOut}`,
    SRM_DATE_TIME_FORMAT
  );

  asset_list.forEach(e => {
    const asset = contract.details.find(
      a =>
        a.detailTrxN.trim() === e.detailTrxN.trim() &&
        a.type !== TRANSACTION_TYPE.SERVICE
    );
    const assetDateOut = moment(
      `${asset.dateOut} ${asset.timeOut}`,
      SRM_DATE_TIME_FORMAT
    );

    if (assetDateOut.isAfter(lineOutDate)) {
      lineOutDate = assetDateOut;
    }
  });

  return lineOutDate;
}

export async function createTicket(ticketType, postData, showMessage = true) {
  try {
    const res = await axios.post(
      `${TICKET_ASSIGN_URL}${ticketType}/`,
      postData
    );
    if (showMessage) {
      Alert.success({
        message: (
          <>
            {capitalize(ticketType)} {i18n.t("ticket_successfully_created")}
            <br />
            <a href={`/tickets/${ticketType}/edit/${res.data.id}`}>
              {i18n.t("view_number")}
              {res.data.short_title}
            </a>
          </>
        )
      });
    }

    let destination = `/tickets/${ticketType}/list`;
    if (res.status === MULTI_STATUS) {
      Alert.error({
        message: res.data.message,
        timeout: ALERT_ERROR_TIMEOUT
      });
      destination = `/tickets/${ticketType}/edit/${res.data.id}`;
    }
    return {
      destination,
      ticketNumber: res.data.short_title,
      ticketId: res.data.id,
      ticketData: res.data
    };
  } catch (err) {
    let message;
    if (err.response.status === INTERNAL_SERVER_ERROR) {
      message = SERVER_ERROR_500;
    } else if (Array.isArray(err.response.data?.items)) {
      let responseMessasge = [];
      err.response.data.items.forEach(e => {
        responseMessasge.push(e.error);
      });
      message = `Server error - ${responseMessasge.join(" - ")}`;
    } else if (Array.isArray(err.response.data?.error?.items)) {
      let responseMessasge = [];
      err.response.data.error.items.forEach(e => {
        responseMessasge.push(e.error);
      });
      message = `Server error - ${responseMessasge.join(" - ")}`;
    } else {
      message = getApiErrorMsg(err);
    }
    Alert.error({
      message: `Failed to create ticket: ${message}`,
      timeout: ALERT_ERROR_TIMEOUT
    });
    return { destination: null, error: message };
  }
  return { destination: null };
}

export async function updateContractAddress(ticketType, oldData, newData) {
  if (!newData.ticketId) return;

  const addressKey =
    ticketType === ticketTypes.PICK_UP ? "origin" : "destination";
  let oldAddress = {};
  let contractNumber = oldData?.contract_n;

  if (oldData.hasOwnProperty("contractNumber")) {
    contractNumber = oldData.contractNumber;
    oldAddress = getFullAddress(oldData.shipInfo);
  } else {
    oldAddress = getTicketFullAddress(oldData, addressKey);
  }

  if (!newData.hasOwnProperty(`${addressKey}_name`)) return;

  const newAddress = getTicketFullAddress(newData, addressKey);
  if (isEqual(oldAddress, newAddress)) return;

  let updateData: any = {
    shipInfo: {
      address1: newData[`${addressKey}_addr1`],
      address2: newData[`${addressKey}_addr2`],
      city: newData[`${addressKey}_city`],
      name: newData[`${addressKey}_name`],
      provState: newData[`${addressKey}_prov_state`],
      postalZip: newData[`${addressKey}_postal_zip`],
      phone: newData[`${addressKey}_phone`],
      siteNumber: ""
    }
  };
  if (oldData.poNumber) {
    updateData.poNumber = oldData.poNumber;
  }

  try {
    await axios.put(`${UPDATE_CONTRACT_URL}/${contractNumber}`, updateData, {
      responseType: "json"
    });
    await axios.post(`${CONTRACTS_URL}/${contractNumber}/comments`, {
      comment: i18n.t(
        "the_contract_site_address_has_been_updated_due_to_a_href_ticket_number_a_address_updated_previous_address_address",
        {
          ticketType: capitalize(ticketType),
          ticketNumber: newData.ticketNumber,
          address: oldAddress ?? ""
        }
      ),
      operator_id: SCS_SRM_UID,
      print: SrmBoolean.False
    });
  } catch (err) {
    console.log(err);
  }
}

export function useBarcodeScannerAssetSearch(setState: Function) {
  useBarcodeScanner(async (event, barcode) => {
    async function getProduct(url) {
      const {
        data: {
          assets,
          parts,
          pages: { total }
        }
      } = await FlaskReq({ url, method: "get" });

      if (total !== PAGE_NUMBER.ONE) return false;

      return (assets || parts)[0];
    }
    const generateUrl = type =>
      `${localStorage.flask_url}/${type}/?barcode=${barcode}`;

    const product =
      (await getProduct(generateUrl("assets"))) ||
      (await getProduct(generateUrl("parts")));

    setState({
      page: PAGE_NUMBER.ONE,
      searchText: product.productNumber
    });
  }, []);
}

export const getTicketIdFromTicketSchedule = ({
  ticket
}: {
  ticket: Models.SchedulingTicket | Models.MapTicket;
}) => {
  if (ticket?.delivery_ticket) {
    return ticket?.delivery_ticket?.id;
  } else if (ticket?.pickup_ticket) {
    return ticket?.pickup_ticket?.id;
  } else if (ticket?.task_ticket) {
    return ticket?.task_ticket?.id;
  }
  return;
};

export const getTicketFromTicketSchedule = ({
  ticket
}: {
  ticket: Models.SchedulingTicket | Models.MapTicket;
}) => {
  if (ticket?.delivery_ticket) {
    return ticket?.delivery_ticket;
  } else if (ticket?.pickup_ticket) {
    return ticket?.pickup_ticket;
  } else if (ticket?.task_ticket) {
    return ticket?.task_ticket;
  }
  return null;
};

export const getSchedulingTicketType = (
  ticket: Models.SchedulingTicket | Models.MapTicket
) => {
  if (ticket?.pickup_ticket) {
    return {
      code: "P",
      label: i18n.t("pickup"),
      name: "pickup"
    };
  } else if (ticket?.delivery_ticket) {
    return {
      code: "D",
      label: i18n.t("delivery"),
      name: "delivery"
    };
  } else if (ticket?.task_ticket) {
    return {
      code: "T",
      label: i18n.t("task"),
      name: "task"
    };
  }
  return {
    code: "",
    label: "",
    name: ""
  };
};

export function getOpenTicketUrl(ticket: SchedulingTicket | Models.MapTicket) {
  return `/tickets/${
    getSchedulingTicketType(ticket)?.name
  }/edit/${encodeURIComponent(getTicketIdFromTicketSchedule({ ticket }))}`;
}

export function getTicketAPIUrl(ticket: SchedulingTicket | Models.MapTicket) {
  let type: string = getSchedulingTicketType(ticket)?.name;
  type = type !== "task" ? `tickets/${type}` : type;
  return `/rest-api/${type}/${getTicketIdFromTicketSchedule({ ticket })}/`;
}

export enum SELECT_CONTRACT_TYPE {
  PICKUP = "create_pickup",
  DELIVERY = "create_delivery"
}

export const checkDriver = (drivers, ticketSelected) => {
  return (drivers || []).find(driver => {
    const driverId = String(ticketSelected?.driver_id);
    const assignedAuthId = String(ticketSelected?.assigned_auth_id);
    return (
      String(driver?.id) === assignedAuthId || String(driver?.id) === driverId
    );
  });
};

export const checkAndAllowDriverAssign = (drivers, ticketSelected) => {
  const assignedDriver = checkDriver(drivers, ticketSelected);
  const hasAssignedDriver = !isNil(assignedDriver?.id);
  const isTicketStatusCreated = ticketSelected.status === TICKET_STATUS.CREATED;
  const isTicketWithoutDriver =
    [
      TICKET_STATUS.ASSIGNED,
      TICKET_STATUS.IN_PROGRESS,
      TICKET_STATUS.LOADING,
      TICKET_STATUS.UNLOADING,
      TICKET_STATUS.PAUSED
    ].includes(ticketSelected.status) && !hasAssignedDriver;

  return {
    allowDriverSelect: isTicketStatusCreated || isTicketWithoutDriver,
    assignedDriver: assignedDriver
  };
};
